<template>
  <section class="dialogs-page">
    <div class="dialogs-toolbar">
      <label class="dialogs-search">
        <el-icon class="dialogs-search__icon"><Search /></el-icon>
        <input v-model.trim="searchQuery" type="text" placeholder="Поиск" />
      </label>

      <button class="dialogs-create-btn" type="button" @click="openCreateDialog">
        <span class="dialogs-create-btn__icon" aria-hidden="true">
          <img src="/img/dialogs/create-user.png" alt="" class="dialogs-create-btn__user" />
          <img src="/img/dialogs/create-plus.png" alt="" class="dialogs-create-btn__plus" />
        </span>
        <span>Создать диалог</span>
      </button>

      <div class="dialogs-filters" role="tablist" aria-label="Фильтры диалогов">
        <button
          v-for="filter in filterOptions"
          :key="filter.value"
          type="button"
          :class="['dialogs-filter', { 'is-active': activeFilter === filter.value }]"
          @click="activeFilter = filter.value"
        >
          {{ filter.label }}
        </button>
      </div>
    </div>

    <div class="dialogs-layout">
      <aside class="dialogs-sidebar">
        <DialogsList
          :dialogs="filteredDialogs"
          :selected-dialog-id="selectedDialog?.id"
          @select-dialog="handleSelectDialog"
        />
      </aside>

      <main class="dialogs-main">
        <ChatPanel :selected-dialog="selectedDialog" />
      </main>
    </div>

    <el-dialog
      v-model="createDialogVisible"
      class="dialogs-create-modal"
      width="520px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <template #header>
        <div class="dialogs-create-modal__title">Создать диалог</div>
      </template>

      <el-form :model="newDialog" label-position="top" class="dialogs-create-form">
        <el-form-item label="Тип мессенджера">
          <el-select v-model="newDialog.type" placeholder="Выберите тип">
            <el-option label="WhatsApp" value="whatsapp" />
            <el-option label="Telegram" value="telegram" disabled />
            <el-option label="Email" value="email" disabled />
          </el-select>
        </el-form-item>

        <el-form-item label="Номер профиля (отправитель)">
          <el-select v-model="newDialog.messenger_id" placeholder="Выберите профиль">
            <el-option
              v-for="messenger in activeAuthorizedMessengers"
              :key="messenger.id"
              :label="messenger.display_name + ' (' + messenger.external_id + ')'"
              :value="messenger.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Клиент">
          <el-autocomplete
            v-model="clientSearch"
            :fetch-suggestions="searchClientsFn"
            placeholder="Телефон или имя"
            @select="selectClient"
            :trigger-on-focus="false"
            style="width: 100%;"
          >
            <template #default="{ item }">
              <div class="autocomplete-client-item">
                <div>{{ item.fio }}</div>
                <div v-if="item.phone">{{ item.phone }}</div>
              </div>
            </template>
          </el-autocomplete>

          <el-card
            v-if="newDialog.client_name || newDialog.client_number"
            class="client-selected-block"
          >
            <div class="client-row">
              <el-icon><UserFilled /></el-icon>
              <span>{{ newDialog.client_name }}</span>
            </div>
            <div v-if="newDialog.client_number" class="client-row">
              <el-icon><Phone /></el-icon>
              <span>{{ newDialog.client_number }}</span>
            </div>
            <el-link
              type="danger"
              class="client-selected-reset"
              @click="clearSelectedClient"
            >Сбросить</el-link>
          </el-card>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button class="dialogs-create-modal__cancel" @click="createDialogVisible = false">Отмена</el-button>
        <el-button class="dialogs-create-modal__submit" type="primary" @click="addNewDialog">Создать</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import DialogsList from '@/components/util/dialogs/DialogsList.vue'
import ChatPanel from '@/components/util/dialogs/ChatPanel.vue'
import { mapActions, mapState } from 'vuex'
import { Phone, Search, UserFilled } from '@element-plus/icons-vue'

export default {
  name: 'DialogsPage',
  components: {
    ChatPanel,
    DialogsList,
    Phone,
    Search,
    UserFilled
  },
  data() {
    return {
      selectedDialog: null,
      createDialogVisible: false,
      newDialog: {
        type: 'whatsapp',
        messenger_id: null,
        client_number: '',
        client_name: '',
        client_external_id: ''
      },
      searchQuery: '',
      clientSearch: '',
      activeFilter: 'all'
    }
  },
  computed: {
    ...mapState('messengers', ['dialogs', 'messengers']),
    ...mapState('clients', ['search_clients']),
    filterOptions() {
      return [
        { value: 'all', label: 'Все' },
        { value: 'unread', label: 'Непрочитанные' },
        { value: 'archive', label: 'Архив' }
      ]
    },
    activeAuthorizedMessengers() {
      return this.messengers.filter(
        messenger =>
          messenger.type === this.newDialog.type &&
          messenger.auth_status === 'authorized' &&
          messenger.active_status === 'active'
      )
    },
    filteredDialogs() {
      const query = this.searchQuery.trim().toLowerCase()
      return (this.dialogs || []).filter(dialog => {
        if (!this.matchesFilter(dialog)) return false
        if (!query) return true
        return this.dialogSearchText(dialog).includes(query)
      })
    }
  },
  watch: {
    dialogs: {
      immediate: true,
      handler(list) {
        if (!Array.isArray(list) || !list.length) return
        if (!this.selectedDialog?.id) return
        const freshDialog = list.find(dialog => String(dialog.id) === String(this.selectedDialog.id))
        if (freshDialog) {
          this.selectedDialog = freshDialog
        }
      }
    }
  },
  async mounted() {
    await this.bootstrapDialogs()
  },
  methods: {
    ...mapActions('messengers', ['createDialogAction', 'fetchDialogs', 'fetchMessengers']),
    ...mapActions('clients', ['searchClients']),
    dialogSearchText(dialog) {
      return [
        dialog.client_name,
        dialog.client_phone,
        dialog.last_message_preview,
        dialog.last_message,
        dialog.last_message_text
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    },
    matchesFilter(dialog) {
      if (this.activeFilter === 'unread') {
        return Number(dialog.unread_count || 0) > 0
      }
      if (this.activeFilter === 'archive') {
        return Boolean(
          dialog.archived ||
          dialog.is_archived ||
          dialog.in_archive ||
          ['archive', 'archived'].includes(String(dialog.status || '').toLowerCase())
        )
      }
      return true
    },
    async bootstrapDialogs() {
      await Promise.all([this.fetchMessengers(), this.fetchDialogs()])

      const params = new URLSearchParams(window.location.search)
      const dialogId = params.get('dialog_id')
      if (dialogId) {
        const dialog = (this.dialogs || []).find(item => String(item.id) === String(dialogId))
        if (dialog) {
          this.handleSelectDialog(dialog)
          return
        }
      }

      if (!this.selectedDialog && this.filteredDialogs.length) {
        this.handleSelectDialog(this.filteredDialogs[0])
      }
    },
    openCreateDialog() {
      this.newDialog = {
        type: 'whatsapp',
        messenger_id: null,
        client_number: '',
        client_name: '',
        client_external_id: ''
      }
      this.clientSearch = ''
      this.createDialogVisible = true
      this.fetchMessengers()
    },
    async addNewDialog() {
      if (!this.newDialog.messenger_id || !this.newDialog.client_number) {
        this.$notify({ type: 'warning', message: 'Заполните все обязательные поля' })
        return
      }

      try {
        const dialogPayload = {
          ...this.newDialog,
          client_external_id: this.newDialog.client_external_id || ''
        }
        const dialogId = await this.createDialogAction(dialogPayload)
        this.$notify({ type: 'success', message: 'Диалог успешно создан' })
        this.createDialogVisible = false

        const createdDialog = (this.dialogs || []).find(dialog => String(dialog.id) === String(dialogId))
        if (createdDialog) {
          this.handleSelectDialog(createdDialog)
        }
      } catch (error) {
        console.error(error)
        this.$notify({ type: 'error', message: 'Ошибка при создании диалога' })
      }
    },
    async searchClientsFn(query, cb) {
      if (!query || query.length < 3) {
        cb([])
        return
      }
      await this.searchClients({ query })
      const results = (this.search_clients || []).map(client => ({
        ...client,
        value: client.fio + (client.phone ? ` (${client.phone})` : '')
      }))
      cb(results)
    },
    selectClient(item) {
      this.newDialog.client_number = item.phone || ''
      this.newDialog.client_name = item.fio || ''
      this.newDialog.client_external_id = item.id || ''
      this.clientSearch = item.fio + (item.phone ? ` (${item.phone})` : '')
    },
    clearSelectedClient() {
      this.newDialog.client_number = ''
      this.newDialog.client_name = ''
      this.newDialog.client_external_id = ''
      this.clientSearch = ''
    },
    handleSelectDialog(dialog) {
      if (!dialog?.id || this.selectedDialog?.id === dialog.id) return
      this.selectedDialog = dialog
      this.$store.commit('socket/SET_SELECTED_DIALOG', dialog)
      this.$store.dispatch('messengers/fetchMessages', { dialog_id: dialog.id })

      const url = new URL(window.location)
      url.searchParams.set('dialog_id', dialog.id)
      window.history.replaceState({}, '', url)
    }
  }
}
</script>

<style scoped>
.dialogs-page {
  --dialogs-sidebar-width: 430px;
  --dialogs-create-width: 274px;
  --dialogs-panel: linear-gradient(180deg, #17366b 0%, #102b58 100%);
  --dialogs-card: linear-gradient(180deg, #153367 0%, #112854 100%);
  --dialogs-input: #0a1838;
  --dialogs-border: rgba(44, 88, 155, 0.95);
  --dialogs-border-soft: rgba(56, 102, 176, 0.42);
  --dialogs-cyan: #22c0ff;
  --dialogs-cyan-soft: rgba(34, 192, 255, 0.18);
  height: 100%;
  min-height: 0;
  max-height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 18px 16px;
  background: #020816;
  overflow: hidden;
}

.dialogs-toolbar {
  display: grid;
  grid-template-columns: minmax(360px, var(--dialogs-sidebar-width)) var(--dialogs-create-width) max-content;
  gap: 12px;
  align-items: center;
  justify-content: start;
}

.dialogs-search {
  width: 100%;
  justify-self: start;
  box-sizing: border-box;
  min-width: 0;
  height: 42px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  background: #06122c;
  border: 1px solid rgba(44, 88, 155, 0.9);
  border-radius: 8px;
}

.dialogs-search__icon {
  color: var(--dialogs-cyan);
  font-size: 18px;
}

.dialogs-search input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #ffffff;
  font-family: var(--ui-font-family-base);
  font-size: 18px;
  font-weight: 300;
}

.dialogs-search input::placeholder {
  color: #344c71;
  opacity: 1;
}

.dialogs-create-btn,
.dialogs-filter {
  height: 42px;
  border-radius: 8px;
  font-family: var(--ui-font-family-base);
  font-size: 18px;
  font-weight: 300;
  transition: 0.2s ease;
}

.dialogs-create-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  background: #22c0ff;
  color: #ffffff;
  width: 100%;
  box-sizing: border-box;
  outline: none;
  box-shadow: none;
}

.dialogs-create-btn__icon {
  position: relative;
  width: 22px;
  height: 18px;
  flex-shrink: 0;
}

.dialogs-create-btn__user,
.dialogs-create-btn__plus {
  position: absolute;
  display: block;
  object-fit: contain;
}

.dialogs-create-btn__user {
  left: 0;
  top: 1px;
  width: 16px;
  height: 16px;
}

.dialogs-create-btn__plus {
  right: 0;
  bottom: -1px;
  width: 12px;
  height: 12px;
}

.dialogs-filters {
  display: grid;
  grid-template-columns: 70px 170px 86px;
  gap: 12px;
  width: max-content;
}

.dialogs-filter {
  border: 1px solid rgba(44, 88, 155, 0.9);
  background: #06122c;
  color: #344c71;
  outline: none;
  box-shadow: none;
}

.dialogs-filter.is-active {
  background: transparent;
  border-color: #22c0ff;
  color: #ffffff;
  box-shadow: none;
}

.dialogs-create-btn:focus-visible,
.dialogs-filter:focus-visible,
.dialogs-search:focus-within {
  outline: none;
  box-shadow: none;
}

.dialogs-layout {
  min-height: 0;
  flex: 1;
  display: grid;
  grid-template-columns: minmax(360px, var(--dialogs-sidebar-width)) minmax(0, 1fr);
  gap: 12px;
}

.dialogs-sidebar,
.dialogs-main {
  min-height: 0;
}

.dialogs-sidebar {
  overflow: hidden;
}

.dialogs-main {
  display: flex;
  min-width: 0;
  overflow: hidden;
}

.autocomplete-client-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-family: var(--ui-font-family-base);
}

.client-selected-block {
  width: 100%;
  margin-top: 12px;
  border-radius: 12px;
}

.client-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.client-row + .client-row {
  margin-top: 6px;
}

.client-selected-reset {
  margin-top: 8px;
}

@media (max-width: 1180px) {
  .dialogs-page {
    padding-inline: 16px;
  }

  .dialogs-toolbar {
    grid-template-columns: minmax(0, 1fr) 274px;
  }

  .dialogs-filters {
    grid-column: 1 / -1;
  }
}

@media (max-width: 900px) {
  .dialogs-page {
    height: 100%;
    min-height: 0;
    max-height: 100%;
    padding: 12px;
  }

  .dialogs-layout {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(260px, 36vh) minmax(0, 1fr);
  }
}

@media (max-width: 640px) {
  .dialogs-toolbar {
    grid-template-columns: 1fr;
  }

  .dialogs-search {
    width: 100%;
  }

  .dialogs-create-btn,
  .dialogs-filter,
  .dialogs-search {
    height: 44px;
  }

  .dialogs-filters {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .dialogs-filter {
    font-size: 15px;
  }
}

@media (max-width: 420px) {
  .dialogs-page {
    padding: 10px;
  }

  .dialogs-filters {
    gap: 6px;
  }

  .dialogs-filter {
    font-size: 13px;
  }
}
</style>

<style>
.dialogs-create-modal {
  --el-dialog-bg-color: #17366b;
}

.dialogs-create-modal.el-dialog {
  width: min(520px, calc(100vw - 18px)) !important;
  background: linear-gradient(180deg, #17366b 0%, #112954 100%) !important;
  border: 1px solid rgba(44, 88, 155, 0.92) !important;
  border-radius: 14px !important;
  box-shadow: 0 24px 54px rgba(0, 8, 24, 0.42) !important;
  overflow: hidden;
}

.dialogs-create-modal .el-dialog__header,
.dialogs-create-modal .el-dialog__body,
.dialogs-create-modal .el-dialog__footer {
  background: transparent;
}

.dialogs-create-modal .el-dialog__header {
  padding: 16px 18px 4px;
}

.dialogs-create-modal .el-dialog__body {
  padding: 8px 18px 14px;
}

.dialogs-create-modal .el-dialog__footer {
  padding: 6px 18px 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.dialogs-create-modal .el-dialog__headerbtn {
  top: 14px;
  right: 14px;
}

.dialogs-create-modal .el-dialog__close {
  color: rgba(224, 238, 255, 0.72) !important;
}

.dialogs-create-modal__title,
.dialogs-create-modal .el-form-item__label {
  color: #ffffff !important;
  font-family: var(--ui-font-family-base) !important;
}

.dialogs-create-modal__title {
  text-align: center;
  font-size: 18px;
  line-height: 1;
}

.dialogs-create-form {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dialogs-create-modal .el-form-item {
  margin-bottom: 12px;
}

.dialogs-create-modal .el-input__wrapper,
.dialogs-create-modal .el-select__wrapper,
.dialogs-create-modal .el-autocomplete .el-input__wrapper {
  background: #0d2248 !important;
  border: none !important;
  box-shadow: 0 0 0 1px rgba(56, 102, 176, 0.72) inset !important;
  border-radius: 8px !important;
  min-height: 40px !important;
}

.dialogs-create-modal .el-input__inner,
.dialogs-create-modal .el-select__placeholder,
.dialogs-create-modal .el-select__selected-item {
  color: #22c0ff !important;
  -webkit-text-fill-color: #22c0ff !important;
  font-family: var(--ui-font-family-base) !important;
}

.dialogs-create-modal .el-input__inner::placeholder {
  color: #22c0ff !important;
  -webkit-text-fill-color: #22c0ff !important;
}

.dialogs-create-modal .el-form-item__label {
  font-size: 13px !important;
  line-height: 1.2 !important;
  padding-bottom: 6px !important;
}

.dialogs-create-modal .el-dialog__footer .el-button {
  min-width: 88px;
  min-height: 32px;
}

.dialogs-create-modal .el-button--default {
  background: transparent !important;
  color: #ffffff !important;
  border: none !important;
  box-shadow: 0 0 0 1px rgba(180, 214, 255, 0.45) inset !important;
}

.dialogs-create-modal .el-button--default:hover,
.dialogs-create-modal .el-button--default:focus,
.dialogs-create-modal .el-button--default:active {
  background: transparent !important;
  color: #ffffff !important;
  box-shadow: 0 0 0 1px rgba(180, 214, 255, 0.7) inset !important;
}

.dialogs-create-modal .el-button--primary {
  border: none !important;
  background: linear-gradient(180deg, #22c0ff 0%, #1caeff 100%) !important;
  box-shadow: none !important;
}

.dialogs-create-modal__cancel,
.dialogs-create-modal__submit {
  font-family: var(--ui-font-family-base) !important;
}

.dialogs-create-modal .client-selected-block {
  border: none !important;
  background: rgba(7, 22, 47, 0.74) !important;
  box-shadow: 0 0 0 1px rgba(56, 102, 176, 0.48) inset !important;
}

.dialogs-create-modal .client-selected-block .el-card__body {
  color: #d8ebff;
}

@media (max-width: 640px) {
  .dialogs-create-modal.el-dialog {
    width: calc(100vw - 12px) !important;
  }

  .dialogs-create-modal .el-dialog__header {
    padding: 14px 12px 4px;
  }

  .dialogs-create-modal .el-dialog__body {
    padding: 8px 12px 12px;
  }

  .dialogs-create-modal .el-dialog__footer {
    padding: 6px 12px 12px;
  }
}
</style>
